import React, { useState } from "react";
import { BsCalendarMinusFill, BsChevronDown, BsChevronUp } from "react-icons/bs";
import { AiOutlineCheck, AiOutlineWarning, AiOutlineFileText, AiOutlineDownload } from "react-icons/ai";
import { useHistory } from "react-router-dom";

export default function SinglePrescription({
  status,
  presName,
  isClinicalRequired,
  authorDetails,
  instruction,
  notes,
  createdDate,
  presId,
  orderStatus
}) {
  const navigate = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const getAuthorName = details => {
    if (!details) return "N/A";
    const full = details?.name?.full || details?.fullName || details?.displayName;
    if (full) return full;
    const firstLast = [details?.name?.first, details?.name?.last].filter(Boolean).join(" ");
    return firstLast || details?.name || "N/A";
  };

  const sanitizeFilename = raw => {
    const fallback = "prescription";
    if (!raw) return fallback;
    const normalized = raw
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9._-]/g, "")
      .replace(/_+/g, "_");
    const trimmed = normalized || fallback;
    return trimmed.slice(0, 80);
  };

  const onClickChervonDown = async () => {
    try {
      setIsExpanded(true);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const { default: jsPDF } = await import("jspdf");
      await import("jspdf-autotable");

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 14;
      let y = 20;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text("Prescription Details", pageWidth / 2, y, { align: "center" });
      y += 12;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(12);
      pdf.text(`Prescription: ${presName || "N/A"}`, margin, y);
      y += 7;
      pdf.text(`Created: ${createdDate || "N/A"}`, margin, y);
      y += 10;

      pdf.setDrawColor(200);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 8;

      pdf.autoTable({
        startY: y,
        headStyles: { fillColor: [0, 128, 96], textColor: 255 },
        head: [["Field", "Value"]],
        body: [
          ["Prescription ID", presId || "N/A"],
          ["Status", status || "N/A"],
          ["Order Status", orderStatus || "N/A"],
          ["Author", getAuthorName(authorDetails)],
          ["Clinician Required", isClinicalRequired ? "Yes" : "No"]
        ],
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 4 },
        margin: { left: margin, right: margin }
      });

      y = (pdf.lastAutoTable?.finalY || y) + 12;

      const addSection = (title, value) => {
        if (y > pageHeight - 20) {
          pdf.addPage();
          y = 20;
        }
        pdf.setFont("helvetica", "bold");
        pdf.text(title, margin, y);
        y += 8;

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(11);
        const text = value || "Not provided.";
        const lines = pdf.splitTextToSize(text, pageWidth - margin * 2);
        lines.forEach(line => {
          if (y > pageHeight - 20) {
            pdf.addPage();
            y = 20;
          }
          pdf.text(line, margin, y);
          y += 6;
        });
        y += 4;
      };

      addSection("Instructions", instruction);
      addSection("Notes", notes);

      pdf.setFontSize(9);
      pdf.setTextColor(150);
      pdf.text(`${new Date().toLocaleString()}`, pageWidth - margin, pageHeight - 10, {
        align: "right"
      });

      const filenameBase = sanitizeFilename(presName);
      pdf.save(`${filenameBase}_${presId || "details"}.pdf`);
    } catch (err) {
      console.error("PDF generation failed", err);
      alert("Unable to generate PDF. Please try again.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className={`prescriptionContent ${isExpanded ? "expanded" : ""}`}>
      <div>
        <p>Status</p>
        <h5>{status}</h5>
        <p>Order Status</p>
        <h5>{orderStatus}</h5>
      </div>
      <div
        className="borderContent-left"
        style={{
          width: "100%"
        }}
      >
        <p>Prescription</p>
        <h5>{presName}</h5>
        {/* <p>Sub -3M</p> */}
      </div>
      <div className="borderContent-kit">
        <p>Author</p>
        {isClinicalRequired ? (
          <h5>
            <span>
              <AiOutlineWarning color="red" />
            </span>{" "}
            clinician Required
          </h5>
        ) : (
          <h5>
            <span>
              <AiOutlineCheck color="green" />
            </span>{" "}
            {getAuthorName(authorDetails)}
          </h5>
        )}
      </div>
      <div>
        <p>Created</p>
        <h5>{createdDate}</h5>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-start"
        }}
      >
        <button
          className="viewDetailsBtn"
          style={{ marginBottom: "1rem" }}
          onClick={() => {
            const photonSandbox = "https://app.neutron.health";
            const photonProd = "https://app.photon.health";
            const url =
              process.env.REACT_APP_PHOTON_ENV === "sandbox" ? `${photonSandbox}/prescriptions/${presId}` : `${photonProd}/prescriptions/${presId}`;

            window.open(url, "_blank");
          }}
        >
          <AiOutlineFileText
            style={{
              fontSize: "1.5rem",
              color: "#007F61",
              cursor: "pointer"
            }}
          />
          <span className="chartingColour">View Prescription</span>
        </button>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end" }}>
          {isExpanded ? (
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                className="viewDetailsBtn"
                onClick={handleDownloadPdf}
                disabled={generatingPdf}
                aria-label="Download prescription PDF"
                title="Download prescription PDF"
              >
                <AiOutlineDownload
                  style={{
                    fontSize: "1.5rem",
                    color: "#007F61",
                    cursor: "pointer"
                  }}
                />
                <span className="chartingColour">{generatingPdf ? "Generating..." : "Download PDF"}</span>
              </button>
              <button className="viewDetailsBtn" onClick={() => setIsExpanded(false)}>
                <BsChevronUp
                  style={{
                    fontSize: "1.5rem",
                    color: "#007F61",
                    cursor: "pointer"
                  }}
                />
                <span className="chartingColour">Close Details</span>
              </button>
            </div>
          ) : (
            <button className="viewDetailsBtn" onClick={onClickChervonDown}>
              <BsChevronDown
                style={{
                  fontSize: "1.5rem",
                  color: "#007F61",
                  cursor: "pointer"
                }}
              />
              <span className="chartingColour"> View Details</span>
            </button>
          )}
        </div>
      </div>
      <div className={`${ true? "resultData" : "gridSpanFullCol"}`}>
        <div className="resultData">
          <div className="resultContainer">
            <p>Instructions</p>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between"
              }}
            >
              {/* <h6>{"Note "}</h6> */}
              <h6>{instruction}</h6>
            </div>
          </div>
          <div className="resultContainer">
            <p>Notes</p>

            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between"
              }}
            >
              {/* <h6>{"Note "}</h6> */}
              <h6>{notes}</h6>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
